import React, { useEffect } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { ArrowUp, ArrowDown, Info } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import { handleClaimDashboardFilter } from "../config/claimDashboardSlice";
import { GetAllClaimDashboard } from "../config/claimDashboardApi";
import { fNumber } from "features/utils";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const StatsDashboard = () => {
  const dispatch = useDispatch();
  const { claimDashboardFilters, claimDashboardData } = useSelector((state) => state.claimDashboard);
  const { control, setValue, watch } = useForm({
    defaultValues: {
      top_frm_mnth: claimDashboardFilters?.top_frm_mnth || "",
    },
  });

  const handleTopFromMonth = (date) => {
    let top_frm_mnth = "";
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      top_frm_mnth = `${year}-${month}`;
    }
    setValue("top_frm_mnth", top_frm_mnth);
    dispatch(handleClaimDashboardFilter({ check: "top_frm_mnth", value: top_frm_mnth }));

    // Call API directly when date picker changes
    const branchesValue = claimDashboardFilters.branches || [];
    const branch_id = branchesValue
      .map((item) => item?.branch_id)
      .filter(Boolean)
      .join(",") || "";

    const frm_mnth = claimDashboardFilters.frm_mnth || "";
    const to_mnth = claimDashboardFilters.to_mnth || claimDashboardFilters.frm_mnth || "";


    dispatch(
      GetAllClaimDashboard({
        branch_id,
        top_frm_mnth,
        frm_mnth,
        to_mnth,
      })
    );

  };

  // Convert month string to Date object for DatePicker
  const getDateFromMonth = (monthStr) => {
    if (!monthStr) return null;
    return new Date(monthStr + "-01");
  };

  const current = claimDashboardData?.claim_current_month || {};
  const prev = claimDashboardData?.claim_previous_month || {
    total: 0,
    tot_submtamount: 0,
    approved: 0,
    rejected: 0,
    tot_rejected_submtamount: 0,
    avg_respmins: 0,
  };

  const totalClaims = current.total || 0;
  const totalValue = current.tot_submtamount || 0;
  const totalApproved = current.approved || 0;
  const totalRejected = current.rejected || 0;
  const totalRejectedAmount = current.tot_rejected_submtamount || 0;
  const avarageProcessing = current.avg_respmins || 0;
  const averageProcessingDays = avarageProcessing ? (avarageProcessing / 1440).toFixed(2) : 0.00;

  const approvalRate = totalClaims ? ((totalApproved / totalClaims) * 100).toFixed(2) : 0;
  const rejectionRate = totalClaims ? ((totalRejected / totalClaims) * 100).toFixed(2) : 0;

  const prevTotalClaims = prev.total || 0;
  const prevTotalValue = prev.tot_submtamount || 0;
  const prevTotalApproved = prev.approved || 0;
  const prevTotalRejected = prev.rejected || 0;
  const prevTotalRejectedAmount = prev.tot_rejected_submtamount || 0;
  const prevAvarageProcessing = prev.avg_respmins || 0;
  const prevAverageProcessingDays = prevAvarageProcessing ? (prevAvarageProcessing / 1440).toFixed(2) : 0.00;

  const prevApprovalRate = prevTotalClaims ? ((prevTotalApproved / prevTotalClaims) * 100).toFixed(2) : 0;
  const prevRejectionRate = prevTotalClaims ? ((prevTotalRejected / prevTotalClaims) * 100).toFixed(2) : 0;


  const ChangeIndicator = ({ currentVal, prevVal, higherBetter, isPercentage = true, isAbsoluteDiff = false, unit = '' }) => {
    if (prevVal === 0 && currentVal === 0) {
      return (
        <div className="d-flex align-items-center fs-11 text-muted">
          <span>No change from last month</span>
        </div>
      );
    }
    if (prevVal === 0) {
      return (
        <div className="d-flex align-items-center fs-11 text-success">
          <ArrowUp className="me-1" />
          <span>New this month</span>
        </div>
      );
    }

    let change;
    if (isAbsoluteDiff) {
      change = currentVal - prevVal;
    } else if (isPercentage) {
      change = ((currentVal - prevVal) / prevVal) * 100;
    } else {
      change = currentVal - prevVal;
    }

    const isIncrease = change > 0;
    const isGood = higherBetter ? isIncrease : !isIncrease;
    const color = isGood ? 'text-success' : 'text-danger';
    const Icon = isIncrease ? ArrowUp : ArrowDown;
    const sign = change > 0 ? '+' : '-';
    const absChange = Math.abs(change).toFixed(isPercentage || isAbsoluteDiff ? 1 : 2);
    const displayUnit = unit ? ` ${unit}` : '';
    const textSuffix = isPercentage ? '%' : '';
    const text = `${sign}${absChange}${textSuffix}${displayUnit} from last month`;

    return (
      <div className={`d-flex align-items-center fs-11 ${color}`}>
        <Icon className="me-1" />
        <span>{text}</span>
      </div>
    );
  };

  return (
    <Card className="shadow-sm rounded-4 p-2">
      <Card.Header className="d-flex justify-content-between align-items-center  border-0 pb-0">
        <Col xs={12}>
          <Row>
            <Col xs={12} sm={12} xl={10}>
              <h5 className="mb-0 fs-9 text-primary fw-bold">Claim Summary</h5>
            </Col>
            <Col xs={12} sm={12} xl={2}>
              <BootDatePicker
                name="top_frm_mnth"
                control={control}
                selected={getDateFromMonth(claimDashboardFilters?.top_frm_mnth)}
                onChange={handleTopFromMonth}
                showMonthYearPicker
                showTodayButton={false}
                dateFormat="MM/yyyy"
                placeholderText="Select month..."
                className="form-control-sm"
              />
            </Col>
          </Row>
        </Col>
      </Card.Header>
      <Card.Body className="pt-3">
        <Row className="g-3">
          {/* Total Claims */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #0d6efd",
              }}
              className="h-100 shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-primary text-uppercase fs-11 mb-2">
                  TOTAL CLAIMS
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Total number of claims submitted this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={16}
                    style={{
                      backgroundColor: "#0d6efd",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mt-3 text-primary mb-1">
                  {totalClaims}
                </h3>
                <ChangeIndicator
                  currentVal={totalClaims}
                  prevVal={prevTotalClaims}
                  higherBetter={true}
                  isPercentage={true}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Total Value */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #0d6efd",
              }}
              className="h-100 shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-primary text-uppercase fs-11 mb-2">
                  TOTAL VALUE
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Total monetary value of claims submitted this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={16}
                    style={{
                      backgroundColor: "#0d6efd",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mb-1 text-primary">{fNumber(totalValue)}</h3>
                <ChangeIndicator
                  currentVal={totalValue}
                  prevVal={prevTotalValue}
                  higherBetter={true}
                  isPercentage={false}
                  isAbsoluteDiff={true}
                  unit="SAR"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Approval Rate */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #0d6efd",
              }}
              className="h-100 shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-primary text-uppercase fs-11 mb-2">
                  APPROVAL RATE
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Percentage of claims approved this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={16}
                    style={{
                      backgroundColor: "#0d6efd",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mb-1 text-primary">{fNumber(approvalRate)}%</h3>
                <ChangeIndicator
                  currentVal={approvalRate}
                  prevVal={prevApprovalRate}
                  higherBetter={true}
                  isPercentage={false}
                  unit="%"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Avg Processing */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #0d6efd",
              }}
              className="h-100 shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-primary text-uppercase fs-11 mb-2">
                  AVG PROCESSING
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Average time taken to process claims this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={16}
                    style={{
                      backgroundColor: "#0d6efd",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mb-1 text-primary">{averageProcessingDays} days</h3>
                <ChangeIndicator
                  currentVal={averageProcessingDays}
                  prevVal={prevAverageProcessingDays}
                  higherBetter={false}
                  isPercentage={false}
                  isAbsoluteDiff={true}
                  unit="days"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Rejected Claims */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #DD2E2E",
              }}
              className="h-100 badge-subtle-danger shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-danger text-uppercase fs-11 mb-2">
                  REJECTED CLAIMS
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Number of claims rejected this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={16}
                    style={{
                      backgroundColor: "#DD2E2E",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mb-1 text-danger">{totalRejected}</h3>
                <ChangeIndicator
                  currentVal={totalRejected}
                  prevVal={prevTotalRejected}
                  higherBetter={false}
                  isPercentage={false}
                  isAbsoluteDiff={true}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Rejection Rate */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #DD2E2E",
              }}
              className="h-100 badge-subtle-danger shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-danger text-uppercase fs-11 mb-2">
                  REJECTION RATE
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Percentage of claims rejected this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={16}
                    style={{
                      backgroundColor: "#DD2E2E",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mb-1 text-danger">{fNumber(rejectionRate)}%</h3>
                <ChangeIndicator
                  currentVal={rejectionRate}
                  prevVal={prevRejectionRate}
                  higherBetter={false}
                  isPercentage={false}
                  isAbsoluteDiff={true}
                  unit="%"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Rejected Amount */}
          <Col xs={6} lg={4} xl>
            <Card
              style={{
                borderLeft: "3px solid #DD2E2E",
              }}
              className="h-100 badge-subtle-danger shadow-sm rounded-4"
            >
              <Card.Body className="p-3">
                <h6 className="text-danger text-uppercase fs-11 mb-2">
                  REJECTED AMOUNT
                  {/* <OverlayTrigger placement="top" overlay={<Tooltip>Total monetary value of rejected claims this month.</Tooltip>}> */}
                  <Info
                    className="ms-1 text-white"
                    size={14}
                    style={{
                      backgroundColor: "#DD2E2E",
                      borderRadius: "50%",
                      padding: "2px",
                      width: "18px",
                      height: "18px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                  {/* </OverlayTrigger> */}
                </h6>
                <h3 className="fw-bold mb-1 text-danger">{fNumber(totalRejectedAmount)}</h3>
                <ChangeIndicator
                  currentVal={totalRejectedAmount}
                  prevVal={prevTotalRejectedAmount}
                  higherBetter={false}
                  isAbsoluteDiff={true}
                  isPercentage={true}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default StatsDashboard;